import { formatInvoiceAmount } from '@/entities/Invoices';
import type { ICourseDebtError } from '@/shared/lib/utils/getCourseDebtError';
import { SafeHtml } from '@/shared/ui/SafeHtml';
import { Section } from '@/shared/ui/Section';

interface CourseDebtNoticeProps {
  debt: ICourseDebtError;
}

/**
 * Показывается вместо общей ошибки, когда доступ к курсу приостановлен из-за долга.
 * Закрыт только этот курс — остальные работают, и об этом стоит сказать прямо,
 * иначе человек решит, что сломалось всё приложение.
 *
 * Сумма и реквизиты приходят прямо в отказе, поэтому запросов блок не делает.
 */
export const CourseDebtNotice = ({ debt }: CourseDebtNoticeProps) => {
  const hasAmount = debt.amountDue !== null && debt.currency !== null;

  return (
    <Section
      typeSection="section"
      className="w-full h-full min-h-[500px] flex items-center justify-center py-10"
    >
      <div className="w-full max-w-[560px] flex flex-col gap-5 rounded-[16px] bg-white px-6 py-6 shadow-sm">
        <div className="flex flex-col gap-2">
          <span className="bg-red w-fit rounded-[6px] px-2 py-[6px] text-sm font-medium text-white">
            Access suspended
          </span>
          <h2 className="text-xl font-medium text-black-secondary">{debt.message}</h2>
          <p className="text-sm text-grey-secondary">
            Only this course is affected — your other courses are still available.
          </p>
        </div>

        {hasAmount && (
          <div className="flex items-baseline justify-between gap-4 rounded-[12px] bg-grey-10 px-4 py-3">
            <span className="text-sm font-medium text-grey-secondary">
              Amount due
              {debt.invoiceNumber && ` · invoice ${debt.invoiceNumber}`}
            </span>
            <span className="text-xl font-medium text-red">
              {formatInvoiceAmount(debt.amountDue, debt.currency)}
            </span>
          </div>
        )}

        {debt.paymentDetails ? (
          <div className="flex flex-col gap-2">
            <h3 className="text-sm font-medium text-black-secondary">Payment details</h3>
            <SafeHtml
              html={debt.paymentDetails}
              className="rounded-[12px] bg-grey-10 px-4 py-3 text-sm text-black-secondary"
            />
          </div>
        ) : (
          <p className="text-sm text-grey-secondary">
            The college accepts payment by bank transfer. Contact the finance team for the transfer
            details.
          </p>
        )}

      </div>
    </Section>
  );
};
